import { createRoot } from 'react-dom/client'
import { WebPlay } from '@playbooks/web-play-component'

import { init as initAnalytics, track } from './services/analytics'
import { init as initLocalization } from './services/localization'
import fomService from './services/fomService'
import addWebPlayCustomCss from './utils/addWebPlayCustomCss'
import getWebPlayInfoFromParentPage from './utils/getWebPlayInfoFromParentPage'
import getQueryParam from './utils/getQueryParam'

import './main.css'
import './i18n'

let isInitialized = false

const urlParams = new URLSearchParams(window.location.search)

const playbookId = getQueryParam('playbookid', urlParams)
const showScore = getQueryParam('showscore', urlParams) !== null
const email = getQueryParam('email', urlParams)
const theme = getQueryParam('theme', urlParams)

const config = {
  cdnUrl: import.meta.env.VITE_CDN_URL,
  serviceBaseUrl: import.meta.env.VITE_MEETING_SERVICE_URL,
  playbooksBackendUrl: import.meta.env.VITE_PLAYBOOKS_BACKEND_URL,
}

const submissionService = {
  createPlay: fomService.createPlay.bind(fomService),
  attachFileToPlay: fomService.attachFileToPlay.bind(fomService),
  attachActionCardToPlay: fomService.attachActionCardToPlay.bind(fomService),
  sendPlayCreatedNotification: fomService.sendPlayCreatedNotification.bind(fomService),
  rollbackPlayCreation: fomService.rollbackPlayCreation.bind(fomService),
  createAnonymousToken: fomService.createAnonymousToken.bind(fomService),
}

// Define that the app is opened in the iframe
const isIframe = window.self !== window.top

// We need to listen to messages if the app opened in iframe to get webplay settings from the parent page.
if (isIframe) {
  window.addEventListener('message', async event => {
    if (event.data?.key === 'webPlayInfo') window.webPlayInfo = event.data.value
    init()
  })
} else {
  init()
}

async function init() {
  if (isInitialized) return
  isInitialized = true

  document.documentElement.classList.toggle(
    'dark',
    theme === 'dark' || (!theme && window.matchMedia('(prefers-color-scheme: dark)').matches),
  )

  const isDarkTheme = document.documentElement.classList.contains('dark')

  try {
    let settings

    const settingsFromParentPage = await getWebPlayInfoFromParentPage()

    if (settingsFromParentPage) {
      settings = settingsFromParentPage
    } else {
      settings = await fomService.getWebPlayInfo(playbookId!)
    }

    if (settings.statusCode) {
      document.getElementById('app')!.innerHTML =
        `<div class="text-red-600 p-4 text-center">Error: ${settings.statusCode}</div>`
      return
    }

    if (settings.weglotApiKey) {
      initLocalization(settings.weglotApiKey)
    }

    initAnalytics(settings, urlParams)

    const actionCard = await fomService.getActionCard({
      email,
      headersCount: settings.headersCount,
      actionCardUrl: settings.actionCardUrl,
      playbookId,
    })

    addWebPlayCustomCss(settings)

    createRoot(document.getElementById('app')!).render(
      <WebPlay
        actionCard={actionCard}
        settings={{ ...settings, isDarkTheme }}
        submissionService={submissionService}
        config={config}
        playbookId={playbookId!}
        showScore={showScore}
        trackFn={track}
      />,
    )
  } catch (error) {
    console.error('Failed to initialize the app:', error)
  }
}
